import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Stack, Typography } from "@mui/material";
import { RiCloseFill } from 'react-icons/ri';
import { HiOutlineSearch } from "react-icons/hi";
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import BlockContainer from "../../components/Container/BlockContainer";
import MainContainer from "../../components/Container/MainContainer";
import BaseImage from "../../components/BaseImage";
import BaseAccordion from "../../components/BaseAccordion";
import BaseIconInput from "../../components/BaseIconInput";
import GeneButtonList from "../../components/GeneButtonList";

import BannerHowItWorks from '../../assets/images/library/banner-how-it-works.jpg';
import { HOW_IT_WORKS_ARTICLES } from "../../constants";

const HowItWorks = () => {
  const navigate = useNavigate();

  const [searchKey, setSearchKey] = useState('');
  const [data, setData] = useState(HOW_IT_WORKS_ARTICLES);
  const [showReminder, setShowReminder] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null); // Track active accordion

  // Handle search functionality
  const handleClickSearch = (e) => {
    const value = e.target.value;
    setSearchKey(value);
    
    const filteredData = HOW_IT_WORKS_ARTICLES.filter(item => 
      item.label.toLowerCase().includes(value.toLowerCase()) || 
      item.description.toLowerCase().includes(value.toLowerCase()) ||
      item.genes.some(gene => gene.includes(value.toUpperCase()))
    );

    setData(filteredData);
    setShowReminder(filteredData.length > 0);
  };

  useEffect(() => {
    let hash = window.location.hash;
    
    if (hash) {
      hash = '#' + getSectionIdFromReportName(decodeURIComponent(hash));
      hash = hash === '#generalactionableresults' ? '#acmgactionable' : hash;
      // Find the target element using the hashtag
      const targetElement = document.querySelector(hash);
      
      if (targetElement) {
          window.scrollTo(0, document.body.scrollHeight);
      }
    }
  }, []);

  const getSectionIdFromReportName = (reportName) => {
    return reportName.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  };

  return (
    <MainContainer
      sx={{
        padding: 0,
        bgcolor: 'background.default',
      }}
    >
      <BlockContainer>
        <Stack
          direction={'column'}
          sx={{
            borderRadius: '16px',
            padding: '24px 16px',
            boxShadow: '0px 2px 10px 0px #5B87EF1A',
            bgcolor: 'background.white',
            height: '100%'
          }}
          spacing={'24px'}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer'
            }}
            onClick={() => navigate(-1)}
          >
            <KeyboardBackspaceIcon 
              sx={{
                color: 'primary.darkGray',
                fontSize: '20px',
                mr: '5px'
              }}
            />
            <Typography variant="body" color='primary.darkGray'>
              Back
            </Typography>
          </Box>
          <Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: { xs: 'center' },
                borderRadius: '8px',
                position: 'relative',
                width: '100%'
              }}
            >
              <BaseImage
                src={BannerHowItWorks}
                sx={{
                  width: '100%',
                  maxHeight: '240px',
                  borderRadius: '8px'
                }}
                sximage={{
                  borderRadius: '8px',
                  objectFit: 'cover'
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  background: 'linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, #000000 100%)',
                  width: '100%',
                  height: '240px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  padding: { xs: '30px 24px', md: '30px 43px' },
                  borderRadius: '8px'
                }}
              >
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: '60px',
                    lineHeight: '90px',
                    textAlign: 'right',
                    color: 'white'
                  }}
                >
                  HOW <br/>IT WORKS
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mt: '24px'
              }}
            >
              <Typography variant="h1">
                How it works
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Box>
                  <BaseIconInput
                    height='48'
                    placeholder='Search'
                    inputStyles={{
                      background: '#4266B833',
                      borderRadius: '40px',
                      color: '#46545F',
                      border: '#4266b800'
                    }}
                    sx={{ maxWidth: '400px' }}
                    startIcon={<HiOutlineSearch size={20} />}
                    endIcon={<RiCloseFill size={24} />}
                    value={searchKey}
                    onChange={handleClickSearch}
                    onClickEndIcon={() => {
                      setSearchKey('');
                      setData(HOW_IT_WORKS_ARTICLES);
                      setShowReminder(false);
                    }}
                  />
                  {showReminder && (
                    <Box sx={{ ml: '8px' }}>
                      <Typography variant="body" color='primary.red'>
                        Scroll down to see search results
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
          
          {/* BaseAccordion List */}
          <Box>
            <Stack container spacing={'16px'}>
              {data.map((article, idx) => {
                const isActive = activeIndex === idx;
                return (
                  <BaseAccordion
                    key={idx}
                    isExpandIcon
                    isExpand={false}
                    item={{
                      title: article.label,
                      description: article.description
                    }}
                    sx={{
                      bgcolor: 'primary.lightGreen1',
                      boxShadow: 'none',
                      padding: '20px',
                      zIndex: isActive ? 3 : 1, // Toggle zIndex based on active state
                      position: 'relative'
                    }}
                    id={getSectionIdFromReportName(article.label)}
                    onClick={() => {setActiveIndex(idx)}}
                  >
                    {article.genes && (
                      <Typography
                        variant="body"
                        color="primary.darkGray"
                        whiteSpace='pre-wrap'
                      >
                        <GeneButtonList genes={article.genes} styleProps={{
                          fontSize: '15px',
                          fontWeight: '500',
                        }}/>
                      </Typography>
                    )}
                  </BaseAccordion>
                );
              })}
            </Stack>
          </Box>
        </Stack>
      </BlockContainer>
    </MainContainer>
  );
}

export default HowItWorks;
